import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  createStoreProduct,
  getHubbleDiscounts,
  getStoreCategories,
  getStoreProducts,
  getStoreSettings,
  getStoreApiPayload,
  isStoreApiSuccess,
  patchHubbleDiscount,
  patchStoreProduct,
  reorderStoreProducts,
} from '../../services/storeApi';
import {
  findDiscountForProduct,
  formatDateTime,
  formatInrShort,
  formatPercent,
  formatProductStockLabel,
  getCategoryLabel,
  getDiscountBrandId,
  getPricingPreview,
  getProductInventory,
  isLimitedStock,
  parseCategoriesList,
  parseDiscountsList,
  PRODUCT_TYPE_LABELS,
} from '../Store/storeLabels';
import { useStoreSession } from '../Store/useStoreSession';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

/** Admin list order: listed first, then sort_order ASC, Hubble before merch, then id DESC. */
function compareAdminProductOrder(a, b) {
  const listedA = a.is_listed ? 0 : 1;
  const listedB = b.is_listed ? 0 : 1;
  if (listedA !== listedB) return listedA - listedB;

  const so = (a.sort_order ?? 0) - (b.sort_order ?? 0);
  if (so !== 0) return so;
  if (a.product_type !== b.product_type) {
    if (a.product_type === 'HUBBLE_GIFT_CARD') return -1;
    if (b.product_type === 'HUBBLE_GIFT_CARD') return 1;
  }
  return (b.id ?? 0) - (a.id ?? 0);
}

function parseSortOrderInput(value, { required = false, fallback } = {}) {
  const trimmed = String(value ?? '').trim();
  if (trimmed === '') {
    if (required) return { error: 'Sort order is required' };
    return { value: fallback };
  }
  const num = Number(trimmed);
  if (!Number.isInteger(num) || num < 0) {
    return { error: 'Sort order must be a non-negative integer' };
  }
  return { value: num };
}

const PAGE_SIZE = 50;

const DEFAULT_MERCH_SORT_ORDER = 10000;

const emptyMerchForm = {
  title: '',
  description: '',
  image_url: '',
  category_key: '',
  product_subtype: '',
  original_price_inr: '',
  fan_energy_price: '',
  badge_type: '',
  badge_label: '',
  redemption_details: '',
  estimated_delivery_days: '77',
  metadata_json: '',
  stock_type: 'UNLIMITED',
  stock_quantity: '',
  sort_order: String(DEFAULT_MERCH_SORT_ORDER),
  is_listed: true,
};

const emptyHubbleForm = {
  title: '',
  category_key: '',
  image_url: '',
  discount_percent: '',
  sort_order: '0',
  is_listed: true,
  is_active: true,
};

const PRICING_ROWS = [
  ['Face value', 'faceValueInr'],
  ['Fan Energy covers', 'fanEnergyCoversInr'],
  ['Cash payable', 'cashPayableInr'],
];

/** Face value split into the Fan Energy-funded discount and the cash the user still pays. */
function PricingSplit({ preview, showFooter = false }) {
  const hasAnyValue = PRICING_ROWS.some(([, key]) => preview?.[key] != null);
  if (!hasAnyValue) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Box sx={{ whiteSpace: 'nowrap' }}>
      {PRICING_ROWS.map(([label, key]) => (
        <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="body2" fontWeight={key === 'cashPayableInr' ? 600 : 400}>
            {formatInrShort(preview[key])}
          </Typography>
        </Box>
      ))}
      {showFooter && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {formatPercent(preview.discountPercent)} off
          {preview.fanEnergyRequired != null ? ` · ${preview.fanEnergyRequired.toLocaleString('en-IN')} FE` : ''}
        </Typography>
      )}
    </Box>
  );
}

/** Boxed version of the split used inside the edit dialog, with the Fan Energy cost spelled out. */
function PricingSplitCard({ preview }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2">Pricing preview</Typography>
        {preview.isProjected && <Chip size="small" label="Unsaved" />}
      </Box>
      <PricingSplit preview={preview} />
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Fan Energy needed
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {preview.fanEnergyRequired != null ? `${preview.fanEnergyRequired.toLocaleString('en-IN')} FE` : '—'}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Spend {preview.fanEnergyRequired != null ? `${preview.fanEnergyRequired.toLocaleString('en-IN')} FE` : 'Fan Energy'} to
        get {formatPercent(preview.discountPercent)} off, and pay the rest in cash.
      </Typography>
    </Paper>
  );
}

/** Discount the app currently applies: an admin override wins, otherwise the Hubble default. */
function savedDiscountPercent(discount) {
  return (
    discount?.effective_discount_percent ??
    discount?.overriden_discount ??
    discount?.default_discount ??
    null
  );
}

function ProductPricingCell({ product, discounts, fanEnergyPerInr }) {
  const discount = findDiscountForProduct(discounts, product);
  const preview = getPricingPreview(product, {
    discountPercent: savedDiscountPercent(discount),
    fanEnergyPerInr,
  });
  return <PricingSplit preview={preview} showFooter={preview.discountPercent != null} />;
}

function StoreProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [listedFilter, setListedFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [merchForm, setMerchForm] = useState(emptyMerchForm);
  const [editHubble, setEditHubble] = useState(null);
  const [hubbleForm, setHubbleForm] = useState(emptyHubbleForm);
  const [discounts, setDiscounts] = useState([]);
  const [fanEnergyPerInr, setFanEnergyPerInr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [dragProductId, setDragProductId] = useState(null);
  const [dragOverProductId, setDragOverProductId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { showSnackbar, handleSessionExpired } = useStoreSession(setSnackbar);

  const customCategories = useMemo(
    () =>
      categories.filter((c) => {
        const src = String(c.source || c.type || '').toLowerCase();
        return !src.includes('hubble');
      }),
    [categories]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes, discountsRes, settingsRes] = await Promise.all([
        getStoreProducts({ limit: PAGE_SIZE, offset }),
        getStoreCategories(),
        getHubbleDiscounts({ limit: 500, offset: 0 }),
        getStoreSettings(),
      ]);

      if (handleSessionExpired(productsRes) || handleSessionExpired(categoriesRes)) return;

      if (isStoreApiSuccess(productsRes)) {
        const payload = getStoreApiPayload(productsRes);
        setProducts(payload?.products || []);
      } else {
        showSnackbar(productsRes?.message || 'Failed to load products', 'error');
        setProducts([]);
      }

      if (isStoreApiSuccess(categoriesRes)) {
        setCategories(parseCategoriesList(categoriesRes));
      } else {
        setCategories([]);
      }

      setDiscounts(isStoreApiSuccess(discountsRes) ? parseDiscountsList(discountsRes) : []);
      setFanEnergyPerInr(
        isStoreApiSuccess(settingsRes) ? getStoreApiPayload(settingsRes)?.fan_energy_per_inr ?? null : null
      );
    } finally {
      setLoading(false);
    }
  }, [offset, handleSessionExpired, showSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredProducts = useMemo(() => {
    let list = products;
    if (typeFilter !== 'ALL') {
      list = list.filter((p) => p.product_type === typeFilter);
    }
    if (listedFilter === 'LISTED') {
      list = list.filter((p) => !!p.is_listed);
    } else if (listedFilter === 'UNLISTED') {
      list = list.filter((p) => !p.is_listed);
    }
    if (categoryFilter) {
      list = list.filter((p) => (p.category_key || '') === categoryFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => (p.title || '').toLowerCase().includes(q));
    }
    return [...list].sort(compareAdminProductOrder);
  }, [products, typeFilter, listedFilter, categoryFilter, search]);

  /** Live catalog rows on this page — reorder only applies to listed products. */
  const listedProducts = useMemo(
    () => filteredProducts.filter((p) => !!p.is_listed),
    [filteredProducts]
  );

  const filtersBlockReorder =
    typeFilter !== 'ALL' ||
    listedFilter === 'UNLISTED' ||
    !!categoryFilter ||
    !!search.trim();
  const canReorder =
    !filtersBlockReorder && offset === 0 && !loading && !saving && listedProducts.length > 0;

  const applyLocalOrder = useCallback((orderedListed) => {
    setProducts((prev) => {
      const byId = new Map(prev.map((p) => [p.id, p]));
      const reordered = orderedListed.map((p, index) => {
        const current = byId.get(p.id) || p;
        return { ...current, sort_order: index, is_listed: true };
      });
      const orderedIds = new Set(reordered.map((p) => p.id));
      const rest = prev.filter((p) => !orderedIds.has(p.id));
      return [...reordered, ...rest].sort(compareAdminProductOrder);
    });
  }, []);

  const persistReorder = useCallback(
    async (orderedIds) => {
      setReordering(true);
      try {
        const res = await reorderStoreProducts(orderedIds);
        if (handleSessionExpired(res)) return;
        if (!isStoreApiSuccess(res)) {
          showSnackbar(res?.message || 'Failed to reorder products', 'error');
          loadData();
          return;
        }
        showSnackbar('Catalog order updated');
        loadData();
      } finally {
        setReordering(false);
      }
    },
    [handleSessionExpired, loadData, showSnackbar]
  );

  const moveListedProduct = useCallback(
    (fromIndex, toIndex) => {
      if (!canReorder || fromIndex === toIndex || toIndex < 0 || toIndex >= listedProducts.length) {
        return;
      }
      const next = [...listedProducts];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      applyLocalOrder(next);
      persistReorder(next.map((p) => p.id));
    },
    [applyLocalOrder, canReorder, listedProducts, persistReorder]
  );

  const handleDragStart = (productId) => {
    if (!canReorder || reordering) return;
    setDragProductId(productId);
  };

  const handleDragOver = (event, productId) => {
    if (!canReorder || reordering) return;
    event.preventDefault();
    setDragOverProductId(productId);
  };

  const handleDrop = (targetProductId) => {
    if (!canReorder || dragProductId == null || dragProductId === targetProductId) {
      setDragProductId(null);
      setDragOverProductId(null);
      return;
    }
    const fromIndex = listedProducts.findIndex((p) => p.id === dragProductId);
    const toIndex = listedProducts.findIndex((p) => p.id === targetProductId);
    setDragProductId(null);
    setDragOverProductId(null);
    if (fromIndex < 0 || toIndex < 0) return;
    moveListedProduct(fromIndex, toIndex);
  };

  const unlistedHubbleCount = useMemo(
    () => products.filter((p) => p.product_type === 'HUBBLE_GIFT_CARD' && !p.is_listed && p.is_active !== false).length,
    [products]
  );

  const handleBulkListHubble = async () => {
    const targets = products.filter((p) => p.product_type === 'HUBBLE_GIFT_CARD' && !p.is_listed);
    if (!targets.length) {
      showSnackbar('No unlisted Hubble gift cards on this page', 'info');
      return;
    }
    if (!window.confirm(`Enable is_listed for ${targets.length} gift card(s) on this page?`)) return;

    setSaving(true);
    let ok = 0;
    try {
      for (const p of targets) {
        const res = await patchStoreProduct(p.id, { is_listed: true, is_active: true });
        if (handleSessionExpired(res)) return;
        if (isStoreApiSuccess(res)) ok += 1;
      }
      showSnackbar(`Listed ${ok} of ${targets.length} gift card(s)`);
      loadData();
    } finally {
      setSaving(false);
    }
  };

  const patchField = async (product, patch) => {
    const res = await patchStoreProduct(product.id, patch);
    if (handleSessionExpired(res)) return;
    if (!isStoreApiSuccess(res)) {
      showSnackbar(res?.message || 'Update failed', 'error');
      return;
    }
    showSnackbar('Product updated');
    loadData();
  };

  // Users only see a product when it is both listed and active, so listing turns on both.
  const handleListedToggle = (product, checked) =>
    patchField(product, checked ? { is_listed: true, is_active: true } : { is_listed: false });

  const handleCreateMerch = async () => {
    const title = merchForm.title.trim();
    const fe = Number(merchForm.fan_energy_price);
    const originalPrice = Number(merchForm.original_price_inr);
    if (!title) {
      showSnackbar('Title is required', 'warning');
      return;
    }
    if (!Number.isFinite(originalPrice) || originalPrice <= 0) {
      showSnackbar('Original price (INR) must be a positive number', 'warning');
      return;
    }
    if (!Number.isInteger(fe) || fe <= 0) {
      showSnackbar('Fan energy price must be a positive integer', 'warning');
      return;
    }

    const body = {
      title,
      original_price_inr: originalPrice,
      fan_energy_price: fe,
      stock_type: merchForm.stock_type,
      is_listed: merchForm.is_listed,
    };
    if (merchForm.category_key) body.category_key = merchForm.category_key;
    if (merchForm.product_subtype.trim()) body.product_subtype = merchForm.product_subtype.trim().toUpperCase();
    if (merchForm.description.trim()) body.description = merchForm.description.trim();
    if (merchForm.image_url.trim()) body.image_url = merchForm.image_url.trim();
    if (merchForm.badge_type.trim()) body.badge_type = merchForm.badge_type.trim().toUpperCase();
    if (merchForm.badge_label.trim()) body.badge_label = merchForm.badge_label.trim();
    if (merchForm.redemption_details.trim()) {
      body.redemption_details = merchForm.redemption_details
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
    }
    if (merchForm.estimated_delivery_days !== '') {
      const eta = Number(merchForm.estimated_delivery_days);
      if (!Number.isInteger(eta) || eta < 0) {
        showSnackbar('Estimated delivery days must be a non-negative integer', 'warning');
        return;
      }
      body.estimated_delivery_days = eta;
    }
    if (merchForm.metadata_json.trim()) {
      try {
        body.metadata = JSON.parse(merchForm.metadata_json);
      } catch {
        showSnackbar('Metadata must be valid JSON', 'warning');
        return;
      }
    }
    if (String(merchForm.stock_type).toUpperCase() === 'LIMITED') {
      const qty = Number(merchForm.stock_quantity);
      if (!Number.isInteger(qty) || qty < 0) {
        showSnackbar('Stock quantity required for LIMITED stock', 'warning');
        return;
      }
      body.stock_quantity = qty;
    }
    const sortParsed = parseSortOrderInput(merchForm.sort_order, {
      fallback: DEFAULT_MERCH_SORT_ORDER,
    });
    if (sortParsed.error) {
      showSnackbar(sortParsed.error, 'warning');
      return;
    }
    if (sortParsed.value != null) body.sort_order = sortParsed.value;

    setSaving(true);
    try {
      const res = await createStoreProduct(body);
      if (handleSessionExpired(res)) return;
      if (!isStoreApiSuccess(res)) {
        showSnackbar(res?.message || 'Create failed', 'error');
        return;
      }
      showSnackbar('Merch product created');
      setCreateOpen(false);
      setMerchForm(emptyMerchForm);
      loadData();
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editProduct) return;
    const fe = Number(merchForm.fan_energy_price);
    const originalPrice = Number(merchForm.original_price_inr);
    if (!Number.isInteger(fe) || fe <= 0) {
      showSnackbar('Fan energy price must be a positive integer', 'warning');
      return;
    }
    if (!Number.isFinite(originalPrice) || originalPrice <= 0) {
      showSnackbar('Original price (INR) must be a positive number', 'warning');
      return;
    }
    const body = {
      title: merchForm.title.trim() || editProduct.title,
      original_price_inr: originalPrice,
      fan_energy_price: fe,
      is_listed: merchForm.is_listed,
      is_active: merchForm.is_active,
      stock_type: merchForm.stock_type,
      description: merchForm.description.trim() || null,
      image_url: merchForm.image_url.trim() || null,
      category_key: merchForm.category_key || null,
      product_subtype: merchForm.product_subtype.trim().toUpperCase() || null,
      badge_type: merchForm.badge_type.trim().toUpperCase() || null,
      badge_label: merchForm.badge_label.trim() || null,
    };
    if (merchForm.redemption_details.trim()) {
      body.redemption_details = merchForm.redemption_details
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
    } else {
      body.redemption_details = [];
    }
    if (merchForm.estimated_delivery_days !== '') {
      const eta = Number(merchForm.estimated_delivery_days);
      if (!Number.isInteger(eta) || eta < 0) {
        showSnackbar('Estimated delivery days must be a non-negative integer', 'warning');
        return;
      }
      body.estimated_delivery_days = eta;
    }
    if (merchForm.metadata_json.trim()) {
      try {
        body.metadata = JSON.parse(merchForm.metadata_json);
      } catch {
        showSnackbar('Metadata must be valid JSON', 'warning');
        return;
      }
    } else {
      body.metadata = {};
    }
    if (String(merchForm.stock_type).toUpperCase() === 'LIMITED') {
      const qty = Number(merchForm.stock_quantity);
      if (!Number.isInteger(qty) || qty < 0) {
        showSnackbar('Stock quantity required for LIMITED stock', 'warning');
        return;
      }
      body.stock_quantity = qty;
    }
    const sortParsed = parseSortOrderInput(merchForm.sort_order, { required: true });
    if (sortParsed.error) {
      showSnackbar(sortParsed.error, 'warning');
      return;
    }
    body.sort_order = sortParsed.value;

    setSaving(true);
    try {
      const res = await patchStoreProduct(editProduct.id, body);
      if (handleSessionExpired(res)) return;
      if (!isStoreApiSuccess(res)) {
        showSnackbar(res?.message || 'Update failed', 'error');
        return;
      }
      showSnackbar('Product saved');
      setEditProduct(null);
      loadData();
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (product) => {
    const { stockType, stockQuantity } = getProductInventory(product);
    setEditProduct(product);
    setMerchForm({
      title: product.title || '',
      description: product.description || '',
      image_url: product.image_url || '',
      category_key: product.category_key || '',
      product_subtype: product.product_subtype || '',
      original_price_inr:
        product.original_price_inr != null
          ? String(product.original_price_inr)
          : product.real_amount_inr != null
            ? String(product.real_amount_inr)
            : '',
      fan_energy_price: String(product.fan_energy_price ?? ''),
      badge_type: product.badge_type || '',
      badge_label: product.badge_label || '',
      redemption_details: Array.isArray(product.redemption_details) ? product.redemption_details.join('\n') : '',
      estimated_delivery_days:
        product.estimated_delivery_days != null ? String(product.estimated_delivery_days) : '77',
      metadata_json:
        product.metadata && typeof product.metadata === 'object' ? JSON.stringify(product.metadata, null, 2) : '',
      stock_type: stockType,
      stock_quantity: stockQuantity != null ? String(stockQuantity) : '',
      sort_order: product.sort_order != null ? String(product.sort_order) : String(DEFAULT_MERCH_SORT_ORDER),
      is_listed: !!product.is_listed,
      is_active: product.is_active !== false,
    });
  };

  const openHubbleEdit = (product) => {
    const discount = findDiscountForProduct(discounts, product);
    const percent = savedDiscountPercent(discount);
    setEditHubble({ product, discount });
    setHubbleForm({
      title: product.title || '',
      category_key: product.category_key || '',
      image_url: product.image_url || '',
      discount_percent: percent != null ? String(percent) : '',
      sort_order: product.sort_order != null ? String(product.sort_order) : '0',
      is_listed: !!product.is_listed,
      is_active: product.is_active !== false,
    });
  };

  const hubblePercentInput = hubbleForm.discount_percent.trim();

  const hubblePreview = useMemo(() => {
    if (!editHubble) return null;
    const fallback = editHubble.discount?.default_discount ?? null;
    const percent = hubblePercentInput === '' ? fallback : Number(hubblePercentInput);
    const preview = getPricingPreview(editHubble.product, { discountPercent: percent, fanEnergyPerInr });
    const saved = savedDiscountPercent(editHubble.discount);
    return { ...preview, isProjected: preview.discountPercent !== saved };
  }, [editHubble, hubblePercentInput, fanEnergyPerInr]);

  const saveHubbleDiscount = async (product, body) => {
    const brandId = getDiscountBrandId(product);
    if (brandId == null) {
      showSnackbar('This gift card has no Hubble brand ID, so the offer could not be updated', 'warning');
      return false;
    }
    const res = await patchHubbleDiscount(brandId, body);
    if (handleSessionExpired(res)) return false;
    if (!isStoreApiSuccess(res)) {
      showSnackbar(res?.message || 'Failed to update the offer', 'error');
      return false;
    }
    return true;
  };

  const handleSaveHubble = async () => {
    if (!editHubble) return;
    const { product, discount } = editHubble;

    const title = hubbleForm.title.trim();
    if (!title) {
      showSnackbar('Title is required', 'warning');
      return;
    }

    let percent = null;
    if (hubblePercentInput !== '') {
      const num = Number(hubblePercentInput);
      if (!Number.isFinite(num) || num < 0 || num > 100) {
        showSnackbar('Offer must be between 0 and 100', 'warning');
        return;
      }
      percent = num;
    }

    const sortParsed = parseSortOrderInput(hubbleForm.sort_order, { required: true });
    if (sortParsed.error) {
      showSnackbar(sortParsed.error, 'warning');
      return;
    }

    setSaving(true);
    try {
      const productRes = await patchStoreProduct(product.id, {
        title,
        category_key: hubbleForm.category_key || null,
        image_url: hubbleForm.image_url.trim() || null,
        sort_order: sortParsed.value,
        is_listed: hubbleForm.is_listed,
        is_active: hubbleForm.is_active,
      });
      if (handleSessionExpired(productRes)) return;
      if (!isStoreApiSuccess(productRes)) {
        showSnackbar(productRes?.message || 'Update failed', 'error');
        return;
      }

      if (percent !== savedDiscountPercent(discount)) {
        const ok = await saveHubbleDiscount(product, { overriden_discount: percent });
        if (!ok) {
          loadData();
          return;
        }
      }

      showSnackbar('Gift card saved');
      setEditHubble(null);
      loadData();
    } finally {
      setSaving(false);
    }
  };

  const handleClearHubbleOverride = async () => {
    if (!editHubble) return;
    setSaving(true);
    try {
      const ok = await saveHubbleDiscount(editHubble.product, { overriden_discount: null });
      if (!ok) return;
      showSnackbar('Offer override cleared');
      setEditHubble(null);
      loadData();
    } finally {
      setSaving(false);
    }
  };

  const categorySelectOptions = customCategories.length > 0 ? customCategories : categories;

  const categoryField = (
    <TextField
      select
      label="Category"
      value={merchForm.category_key}
      onChange={(e) => setMerchForm((f) => ({ ...f, category_key: e.target.value }))}
      helperText={
        customCategories.length === 0
          ? 'No custom categories — create one under Store → Categories'
          : 'Assign merch to a custom category for the app catalog'
      }
    >
      <MenuItem value="">None</MenuItem>
      {categorySelectOptions.map((c) => (
        <MenuItem key={c.key} value={c.key}>
          {c.label || c.key} ({c.key})
        </MenuItem>
      ))}
    </TextField>
  );

  const merchFormFields = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <TextField label="Title" required value={merchForm.title} onChange={(e) => setMerchForm((f) => ({ ...f, title: e.target.value }))} />
      {categoryField}
      <TextField
        label="Product subtype"
        placeholder="JERSEY"
        value={merchForm.product_subtype}
        onChange={(e) => setMerchForm((f) => ({ ...f, product_subtype: e.target.value.toUpperCase() }))}
      />
      <TextField label="Description" multiline minRows={2} value={merchForm.description} onChange={(e) => setMerchForm((f) => ({ ...f, description: e.target.value }))} />
      <TextField label="Image URL" value={merchForm.image_url} onChange={(e) => setMerchForm((f) => ({ ...f, image_url: e.target.value }))} />
      <TextField
        label="Original price (INR)"
        type="number"
        required
        value={merchForm.original_price_inr}
        onChange={(e) => setMerchForm((f) => ({ ...f, original_price_inr: e.target.value }))}
      />
      <TextField label="Fan energy price" type="number" required value={merchForm.fan_energy_price} onChange={(e) => setMerchForm((f) => ({ ...f, fan_energy_price: e.target.value }))} />
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <TextField
          label="Badge type"
          placeholder="TRENDING"
          value={merchForm.badge_type}
          onChange={(e) => setMerchForm((f) => ({ ...f, badge_type: e.target.value.toUpperCase() }))}
        />
        <TextField
          label="Badge label"
          placeholder="Trending"
          value={merchForm.badge_label}
          onChange={(e) => setMerchForm((f) => ({ ...f, badge_label: e.target.value }))}
        />
      </Box>
      <TextField
        label="Redemption details (one per line)"
        multiline
        minRows={3}
        value={merchForm.redemption_details}
        onChange={(e) => setMerchForm((f) => ({ ...f, redemption_details: e.target.value }))}
      />
      <TextField
        label="Estimated delivery days"
        type="number"
        value={merchForm.estimated_delivery_days}
        onChange={(e) => setMerchForm((f) => ({ ...f, estimated_delivery_days: e.target.value }))}
      />
      <TextField
        label="Metadata (JSON)"
        multiline
        minRows={3}
        value={merchForm.metadata_json}
        onChange={(e) => setMerchForm((f) => ({ ...f, metadata_json: e.target.value }))}
      />
      <TextField
        label="Sort order"
        type="number"
        value={merchForm.sort_order}
        onChange={(e) => setMerchForm((f) => ({ ...f, sort_order: e.target.value }))}
        helperText="Lower = earlier in the app catalog. Merch default is 10000 (after Hubble coupons)."
        inputProps={{ min: 0, step: 1 }}
      />
      <TextField select label="Stock type" value={merchForm.stock_type} onChange={(e) => setMerchForm((f) => ({ ...f, stock_type: e.target.value }))}>
        <MenuItem value="UNLIMITED">Unlimited</MenuItem>
        <MenuItem value="LIMITED">Limited</MenuItem>
      </TextField>
      {String(merchForm.stock_type).toUpperCase() === 'LIMITED' && (
        <TextField label="Stock quantity" type="number" value={merchForm.stock_quantity} onChange={(e) => setMerchForm((f) => ({ ...f, stock_quantity: e.target.value }))} />
      )}
      <FormControlLabel control={<Switch checked={merchForm.is_listed} onChange={(e) => setMerchForm((f) => ({ ...f, is_listed: e.target.checked }))} />} label="Listed in app" />
      {editProduct && (
        <FormControlLabel control={<Switch checked={merchForm.is_active} onChange={(e) => setMerchForm((f) => ({ ...f, is_active: e.target.checked }))} />} label="Active" />
      )}
    </Box>
  );

  return (
    <div>
      <Component>
        <div className="page-body">
          <div className="container-fluid">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-6">
                  <h3>Store Products</h3>
                  <Typography variant="body2" color="text.secondary">
                    Hubble gift cards (sync) · Dugout merch with category_key
                  </Typography>
                </div>
              </div>
            </div>
          </div>

          <div className="container-fluid">
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Button component={Link} to="/admin/store" startIcon={<ArrowBackIcon />} variant="outlined" size="small">
                        Store dashboard
                      </Button>
                      <Button component={Link} to="/admin/store/categories" variant="outlined" size="small">
                        Categories
                      </Button>
                      <Button component={Link} to="/admin/store/discounts" variant="outlined" size="small">
                        Discounts
                      </Button>
                      <Button variant="contained" onClick={() => { setMerchForm(emptyMerchForm); setCreateOpen(true); }}>
                        Add merch product
                      </Button>
                      <Button variant="outlined" onClick={handleBulkListHubble} disabled={saving || loading || unlistedHubbleCount === 0}>
                        Bulk list gift cards ({unlistedHubbleCount})
                      </Button>
                      <TextField select size="small" label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ minWidth: 160 }}>
                        <MenuItem value="ALL">All types</MenuItem>
                        <MenuItem value="HUBBLE_GIFT_CARD">Hubble gift cards</MenuItem>
                        <MenuItem value="DUGOUT_PRODUCT">Dugout merch</MenuItem>
                      </TextField>
                      <TextField
                        select
                        size="small"
                        label="Listed"
                        value={listedFilter}
                        onChange={(e) => setListedFilter(e.target.value)}
                        sx={{ minWidth: 140 }}
                      >
                        <MenuItem value="ALL">All</MenuItem>
                        <MenuItem value="LISTED">Listed (live)</MenuItem>
                        <MenuItem value="UNLISTED">Unlisted</MenuItem>
                      </TextField>
                      <TextField
                        select
                        size="small"
                        label="Category"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        sx={{ minWidth: 160 }}
                      >
                        <MenuItem value="">All categories</MenuItem>
                        {categories.map((c) => (
                          <MenuItem key={c.key} value={c.key}>
                            {c.label || c.key}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField size="small" label="Search title" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 200 }} />
                      <Button variant="text" onClick={loadData} disabled={loading || reordering}>
                        Refresh
                      </Button>
                    </Box>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      Admin list: <strong>listed</strong> products first (live app catalog), then unlisted.
                      Within each group: lower <strong>sort_order</strong>, then Hubble before merch.
                      {canReorder
                        ? ' Drag or use arrows on listed rows to set catalog order (PATCH /products/reorder).'
                        : ' Clear type/category/search filters (Listed = All or Listed is fine) and stay on page 1 to drag-reorder, or set Order in Edit.'}
                    </Alert>

                    {customCategories.length === 0 && !loading && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        No custom merch categories yet.{' '}
                        <Link to="/admin/store/categories">Create categories</Link> before adding jerseys/merch.
                      </Alert>
                    )}

                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell width={40} />
                                <TableCell>Order</TableCell>
                                <TableCell>ID</TableCell>
                                <TableCell sx={{ minWidth: 220 }}>Product</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell align="right">FE price</TableCell>
                                <TableCell sx={{ minWidth: 210 }}>Pricing</TableCell>
                                <TableCell align="center">Listed</TableCell>
                                <TableCell align="center">Active</TableCell>
                                <TableCell>Stock</TableCell>
                                <TableCell>Updated</TableCell>
                                <TableCell align="right">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredProducts.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={12} align="center">
                                    No products found
                                  </TableCell>
                                </TableRow>
                              ) : (
                                filteredProducts.map((p, index) => {
                                  const isListed = !!p.is_listed;
                                  const listedIndex = isListed
                                    ? listedProducts.findIndex((lp) => lp.id === p.id)
                                    : -1;
                                  const rowCanReorder = canReorder && isListed;
                                  const isDragOver = rowCanReorder && dragOverProductId === p.id;
                                  const showUnlistedDivider =
                                    !isListed &&
                                    (index === 0 || !!filteredProducts[index - 1]?.is_listed);

                                  return (
                                    <React.Fragment key={p.id}>
                                      {showUnlistedDivider && (
                                        <TableRow>
                                          <TableCell
                                            colSpan={12}
                                            sx={{ bgcolor: 'action.hover', py: 1 }}
                                          >
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                              Unlisted — not shown in the app catalog
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                      <TableRow
                                        hover
                                        draggable={rowCanReorder && !reordering}
                                        onDragStart={() => {
                                          if (rowCanReorder) handleDragStart(p.id);
                                        }}
                                        onDragEnd={() => {
                                          setDragProductId(null);
                                          setDragOverProductId(null);
                                        }}
                                        onDragOver={(e) => {
                                          if (rowCanReorder) handleDragOver(e, p.id);
                                        }}
                                        onDrop={() => {
                                          if (rowCanReorder) handleDrop(p.id);
                                        }}
                                        sx={{
                                          opacity: reordering && isListed ? 0.7 : 1,
                                          bgcolor: isDragOver
                                            ? 'action.hover'
                                            : !isListed
                                              ? 'grey.50'
                                              : undefined,
                                          cursor: rowCanReorder ? 'grab' : 'default',
                                        }}
                                      >
                                        <TableCell>
                                          <DragIndicatorIcon
                                            fontSize="small"
                                            color={rowCanReorder ? 'action' : 'disabled'}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2" fontFamily="monospace">
                                            {p.sort_order ?? '—'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>{p.id}</TableCell>
                                        <TableCell>
                                          <Typography variant="body2" fontWeight={500}>
                                            {p.title}
                                          </Typography>
                                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5 }}>
                                            <Chip
                                              size="small"
                                              label={PRODUCT_TYPE_LABELS[p.product_type] || p.product_type}
                                              color={p.product_type === 'DUGOUT_PRODUCT' ? 'primary' : 'default'}
                                            />
                                            {p.product_subtype && (
                                              <Chip size="small" variant="outlined" label={p.product_subtype} />
                                            )}
                                          </Box>
                                          {p.hubble_product_id && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                              Hubble: {p.hubble_product_id}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {p.category_key ? (
                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              label={getCategoryLabel(categories, p.category_key)}
                                            />
                                          ) : (
                                            '—'
                                          )}
                                        </TableCell>
                                        <TableCell align="right">{p.fan_energy_price}</TableCell>
                                        <TableCell>
                                          <ProductPricingCell
                                            product={p}
                                            discounts={discounts}
                                            fanEnergyPerInr={fanEnergyPerInr}
                                          />
                                        </TableCell>
                                        <TableCell align="center">
                                          <Switch
                                            size="small"
                                            checked={isListed}
                                            onChange={(e) => handleListedToggle(p, e.target.checked)}
                                            disabled={reordering}
                                          />
                                        </TableCell>
                                        <TableCell align="center">
                                          <Switch
                                            size="small"
                                            checked={p.is_active !== false}
                                            onChange={(e) => patchField(p, { is_active: e.target.checked })}
                                            disabled={reordering}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          {isLimitedStock(p) ? (
                                            <Chip
                                              size="small"
                                              color={
                                                getProductInventory(p).stockQuantity === 0 ? 'error' : 'warning'
                                              }
                                              label={formatProductStockLabel(p)}
                                            />
                                          ) : (
                                            formatProductStockLabel(p)
                                          )}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                          {formatDateTime(p.updated_at)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                          <Tooltip title="Move up">
                                            <span>
                                              <IconButton
                                                size="small"
                                                disabled={
                                                  !rowCanReorder ||
                                                  reordering ||
                                                  listedIndex <= 0
                                                }
                                                onClick={() =>
                                                  moveListedProduct(listedIndex, listedIndex - 1)
                                                }
                                              >
                                                <KeyboardArrowUpIcon fontSize="small" />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                          <Tooltip title="Move down">
                                            <span>
                                              <IconButton
                                                size="small"
                                                disabled={
                                                  !rowCanReorder ||
                                                  reordering ||
                                                  listedIndex < 0 ||
                                                  listedIndex >= listedProducts.length - 1
                                                }
                                                onClick={() =>
                                                  moveListedProduct(listedIndex, listedIndex + 1)
                                                }
                                              >
                                                <KeyboardArrowDownIcon fontSize="small" />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                          <Button
                                            size="small"
                                            disabled={reordering}
                                            onClick={() =>
                                              p.product_type === 'HUBBLE_GIFT_CARD'
                                                ? openHubbleEdit(p)
                                                : openEdit(p)
                                            }
                                          >
                                            Edit
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    </React.Fragment>
                                  );
                                })
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {reordering && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="caption" color="text.secondary">
                              Saving catalog order…
                            </Typography>
                          </Box>
                        )}

                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                          <Button disabled={offset === 0 || reordering} onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}>
                            Previous
                          </Button>
                          <Button disabled={products.length < PAGE_SIZE || reordering} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                            Next
                          </Button>
                          <Typography variant="body2" sx={{ alignSelf: 'center', ml: 1 }}>
                            Offset {offset}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create Dugout merch product</DialogTitle>
          <DialogContent>{merchFormFields}</DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateMerch} disabled={saving}>
              {saving ? 'Creating…' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!editProduct} onClose={() => setEditProduct(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit product #{editProduct?.id}</DialogTitle>
          <DialogContent>{merchFormFields}</DialogContent>
          <DialogActions>
            <Button onClick={() => setEditProduct(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!editHubble} onClose={() => setEditHubble(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit gift card #{editHubble?.product?.id}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Alert severity="info">
                Catalog details come from the Hubble sync. The offer below is the share of face value that Fan
                Energy can cover — the rest is paid in cash.
              </Alert>

              <TextField
                label="Title"
                required
                value={hubbleForm.title}
                onChange={(e) => setHubbleForm((f) => ({ ...f, title: e.target.value }))}
              />

              <TextField
                select
                label="Category"
                value={hubbleForm.category_key}
                onChange={(e) => setHubbleForm((f) => ({ ...f, category_key: e.target.value }))}
              >
                <MenuItem value="">None</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.key} value={c.key}>
                    {c.label || c.key}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Image URL"
                value={hubbleForm.image_url}
                onChange={(e) => setHubbleForm((f) => ({ ...f, image_url: e.target.value }))}
              />

              <TextField
                label="Offer / Fan Energy discount (%)"
                type="number"
                value={hubbleForm.discount_percent}
                onChange={(e) => setHubbleForm((f) => ({ ...f, discount_percent: e.target.value }))}
                inputProps={{ min: 0, max: 100, step: 1 }}
                helperText={
                  editHubble?.discount
                    ? `0–100. Hubble default is ${formatPercent(editHubble.discount.default_discount)}. Leave empty to use the default.`
                    : '0–100. Sync Hubble discounts first if this gift card has no brand discount yet.'
                }
              />

              <TextField
                label="Sort order"
                type="number"
                value={hubbleForm.sort_order}
                onChange={(e) => setHubbleForm((f) => ({ ...f, sort_order: e.target.value }))}
                helperText="Lower = earlier in the app catalog. Hubble coupons typically use 0."
                inputProps={{ min: 0, step: 1 }}
              />

              {hubblePreview && <PricingSplitCard preview={hubblePreview} />}

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hubbleForm.is_listed}
                      onChange={(e) => setHubbleForm((f) => ({ ...f, is_listed: e.target.checked }))}
                    />
                  }
                  label="Listed in app"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={hubbleForm.is_active}
                      onChange={(e) => setHubbleForm((f) => ({ ...f, is_active: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
              </Box>

              {(!hubbleForm.is_listed || !hubbleForm.is_active) && (
                <Alert severity="warning">Users only see this gift card when it is both listed and active.</Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
            <Button
              color="error"
              onClick={handleClearHubbleOverride}
              disabled={saving || editHubble?.discount?.overriden_discount == null}
            >
              Clear override
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => setEditHubble(null)}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveHubble} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Component>
    </div>
  );
}

export default StoreProducts;
